import {
  AlertTriangle, CheckCircle, ChevronDown, Clock, CornerDownRight, Download, Edit, ExternalLink, Lock, MoreVertical,
  Repeat, User as UserIcon, UserPlus, UserX, Users, X,
} from 'react-feather';
import { DocumentNode } from 'graphql';
import { ReactNode, useState } from 'react';
import { TFunction } from 'i18next';
import { Trans, useTranslation } from 'react-i18next';
import { differenceInMinutes } from 'date-fns';
import styled, { css } from 'styled-components';

import { LotteryStatus, ProductType } from '__generated__/graphql';

import { BasicRegistration, RegistrationSummary } from '../helpers';
import { DropdownSection } from '../../Common/Layout';
import { assetUrl, countItems, guessCountry, mergeUrlParams } from '../../../common/helpers';
import { useNotifier } from '../../../common/Notifications/NotificationProvider';
import { useParticipant } from '../ParticipantProvider';
import ConfirmLotteryForm from './ConfirmLotteryForm';
import EditRegistrationForm from './EditRegistrationForm';
import ExchangeInfo from './ExchangeInfo';
import GetRegistrationDetailsQuery from './GetRegistrationDetailsQuery';
import Guard from '../Guard';
import OptionsDropdown, { Option } from '../../../common/Common/OptionsDropdown';
import ParticipantFieldValue from '../../../common/ParticipantFields/ParticipantFieldValue';
import RegistrationAttribute from '../../../common/Registrations/RegistrationAttribute';
import ResaleForm from './ResaleForm';
import SellableLabel from '../../../common/Common/SellableLabel';
import TransferForm from './TransferForm';
import UI from '../../../common/UI';
import UnassignRegistrationForm from './UnassignRegistrationForm';
import UpgradeDetails from './UpgradeDetails';
import useLocale from '../../../common/useLocale';
import useProject from '../../useProject';
import useQuery from '../../../api/useQuery';
import useScroll from '../../../common/useScroll';
import useTimeSlotFormatter from '../../../common/Tickets/useTimeSlotFormatter';

export interface RegistrationDetailsProps {
  registration: BasicRegistration;
  summary: RegistrationSummary;
  action?: Action | null;
  refetchQueries?: DocumentNode[];
}

const RegistrationDetails = ({
  registration,
  summary,
  action: initialAction = null,
  refetchQueries,
}: RegistrationDetailsProps) => {
  const { t } = useTranslation();

  const containerId = `Registration_${registration.id}`;

  const { preregistration } = registration;

  const isOwner = registration.owner.id === summary.participant.id;
  const shouldConfirmLottery = preregistration?.lottery_status === LotteryStatus.selected;
  const shouldAssign = !registration.assigned && registration.is_reassignable && isOwner;

  const primaryAction = shouldConfirmLottery ? 'confirm_lottery' : (shouldAssign ? 'assign_ticket' : 'overview');

  const [action, setAction] = useState<Action | null>(initialAction);

  const isOpen = !!action;

  return (
    <DropdownSection
      open={isOpen}
      onToggle={() => setAction((action) => (action ? null : primaryAction))}
      title={`${t('ticket')} ATL${registration.sequence}`}
      id={containerId}
      sc={{ pr: isOpen ? [3, 4] : [2, 3] }}
      preview={(
        <RegistrationTitle
          registration={registration}
          summary={summary}
          primaryAction={primaryAction}
          activeAction={action}
          setAction={(newAction) => {
            if (newAction === action) {
              setAction(() => (newAction === primaryAction ? null : newAction));
            } else {
              setAction(newAction);
            }
          }}
        />
      )}
      body={(
        <RegistrationBody
          registrationId={registration.id}
          summary={summary}
          containerId={containerId}
          action={action}
          setAction={setAction}
          refetchQueries={refetchQueries}
        />
      )}
    />
  );
};

// eslint-disable-next-line max-len
type Action = 'overview' | 'edit_registration' | 'exchange_ticket' | 'assign_ticket' | 'resell_ticket' | 'unassign_ticket' | 'confirm_lottery';

interface RegistrationBodyProps {
  registrationId: string;
  summary: RegistrationSummary;
  containerId: string;
  action: Action;
  setAction: (action: Action) => void;
  refetchQueries?: DocumentNode[];
}

const RegistrationBody = ({
  registrationId,
  summary,
  containerId,
  action,
  setAction,
  refetchQueries,
}: RegistrationBodyProps) => {
  const { t } = useTranslation();
  const { participant } = useParticipant();
  const { formatDate, parseDate } = useLocale();
  const { scrollToIfInvisible, scrollTo } = useScroll();
  const notifier = useNotifier();
  const project = useProject();
  const formatTimeSlot = useTimeSlotFormatter();

  const { data } = useQuery(
    GetRegistrationDetailsQuery,
    {
      variables: {
        participant: summary.participant.id,
        event: summary.event.id,
        token: summary.view_token,
        registration: registrationId,
      },
    },
  );

  const registration = data?.registrationSummary.registration;

  if (!registration) {
    return (
      <UI.Div sc={{ padding: [3, 4] }}>
        <UI.PageLoader />
      </UI.Div>
    );
  }

  const participants = participant && (registration.participant.id !== participant.id || !registration.assigned)
    ? [participant]
    : [];

  const visibleUpgrades = registration.upgrades.filter((upgrade) => (
    upgrade.product.type === ProductType.item
  ));

  const defaultCountry = guessCountry(project);

  const candidates = participants.map((participant) => ({
    participant: {
      email: participant.email,
      first_name: participant.first_name,
      last_name: participant.last_name,
    },
    details: {
      date_of_birth: participant.date_of_birth,
      gender: participant.gender,
      nationality: participant.nationality || defaultCountry,
      street: participant.street,
      house_number: participant.house_number,
      zip_code: participant.zip_code,
      city: participant.city,
      extra_address_line: participant.extra_address_line,
      country: participant.country || defaultCountry,
      phone: participant.phone,
      emergency_phone: participant.emergency_phone,
    },
  }));

  const isLocked = registration.locked;
  const isOwner = registration.owner.id === summary.participant.id;

  const activeAction = action;

  const handleSetAction = (action: Action) => {
    setAction(action);
    scrollToIfInvisible(`Registration_${registration.id}`, { gutter: 1 });
  };

  const handleEdit = () => {
    setAction('overview');
    scrollToIfInvisible(containerId, { gutter: 1 });
    notifier.success(t('information_saved'));
  };

  const handleUnassign = () => {
    scrollToIfInvisible('UnassignedRegistrationsSection', { gutter: 1 });
    notifier.success(t('ticket_unassigned'));
  };

  const handleCancel = () => {
    setAction('overview');
  };

  return (
    <UI.Div>
      <UI.FlexContainer sc={{ alignItems: 'center', justifyContent: 'space-between', padding: [3, 4] }}>
        <UI.H4>
          {getActionTitle(activeAction, registration, t)}
        </UI.H4>
        <UI.Button
          sc={{
            size: 'sm', blank: true, fontSize: 3, color: 'gray.500', my: -1,
          }}
          onClick={() => setAction(null)}
        >
          <UI.Icon>
            <X />
          </UI.Icon>
        </UI.Button>
      </UI.FlexContainer>
      <UI.HR sc={{ mx: [3, 4], my: 0 }} style={{ borderTopWidth: 2 }} />
      {activeAction === 'overview' && (
        <UI.FadeIn sc={{ padding: [3, 4] }}>
          <UI.Delimit delimiter={<UI.HR sc={{ dashed: true }} />}>
            {isLocked && (
              <UI.Info icon={<Lock />} sc={{ mb: [3, 4] }}>
                {t('registration_locked_info')}
              </UI.Info>
            )}
            {(registration.time_slot || registration.ticket.upcoming_time_slots.length > 0) && (
              <UI.Div>
                <UI.Legend sc={{ fontWeight: 500 }}>
                  {t('time_slot')}
                </UI.Legend>
                {registration.time_slot && (
                  <>
                    <UI.Icon>
                      <Clock />
                    </UI.Icon>
                    {' '}
                    <UI.Delimit>
                      {formatTimeSlot(registration.time_slot)}
                      {registration.time_slot.title}
                    </UI.Delimit>
                  </>
                )}
                {!registration.time_slot && registration.ticket.upcoming_time_slots.length > 0 && (
                  <UI.Span sc={{ color: 'error' }}>
                    <UI.Icon>
                      <AlertTriangle />
                    </UI.Icon>
                    {' '}
                    {t('no_time_slot_chosen')}
                  </UI.Span>
                )}
              </UI.Div>
            )}

            {summary.event.ticket_numbers_published && registration.ticket_number && (
              <UI.Div>
                <UI.Div>
                  <UI.Legend sc={{ fontWeight: 500, mb: 1 }}>
                    {t('ticket_number')}
                  </UI.Legend>
                  <UI.Label sc={{ fontWeight: 500, fontSize: 3 }}>
                    <UI.Icon>
                      <UI.Icons.StartFinish />
                    </UI.Icon>
                    {' '}
                    {registration.ticket_number}
                  </UI.Label>
                </UI.Div>
                {(registration.start_time || registration.corral_name) && (
                  <UI.Div sc={{ mt: 1 }}>
                    <UI.Delimit>
                      {registration.corral_name}
                      {registration.start_time && formatDate(
                        parseDate(registration.start_time, { format: 'internal_time' }),
                        { format: 'display_time' },
                      )}
                    </UI.Delimit>
                  </UI.Div>
                )}
              </UI.Div>
            )}

            {(registration.ticket.allow_create_team || registration.ticket.allow_join_team) && (
              <UI.Div>
                <UI.Legend sc={{ fontWeight: 500 }}>
                  {t('team')}
                </UI.Legend>

                {registration.team && (
                  <>
                    <UI.Icon>
                      <Users />
                    </UI.Icon>
                    {' '}
                    <UI.Delimit>
                      {registration.team.title}
                      {registration.team.captain.id === summary.participant.id && (
                        <UI.Link
                          to={mergeUrlParams({
                            teams: { id: registration.team.id, search: registration.team.title },
                          })}
                          onClick={() => scrollTo('TeamsSection', { gutter: 1 })}
                        >
                          {t('view_team')}
                        </UI.Link>
                      )}
                    </UI.Delimit>
                  </>
                )}
                {!registration.team && (
                  <UI.Span sc={{ muted: true }}>
                    {t('no_team')}
                  </UI.Span>
                )}
              </UI.Div>
            )}

            {!registration.team && registration.group_members.length > 1 && (
              <UI.Div>
                <UI.Legend sc={{ fontWeight: 500 }}>
                  {t('group')}
                </UI.Legend>

                <UI.OL>
                  {registration.group_members.map((member) => (
                    <UI.LI sc={{ muted: !member.full_name }} key={member.id}>
                      {member.full_name || t('not_assigned_yet')}
                    </UI.LI>
                  ))}
                </UI.OL>
              </UI.Div>
            )}

            <Guard scopedParticipant={summary.participant} showModal={false} title={t('your_personal_details')}>
              <UI.GridContainer>
                <UI.Div>
                  <UI.Legend sc={{ fontWeight: 500 }}>{`${t('email_address')}`}</UI.Legend>
                  {registration.participant.email}
                </UI.Div>

                {registration.required_attributes.map((attribute) => (
                  <RegistrationAttribute
                    registration={registration}
                    attribute={attribute}
                    showEmptyWarning
                    key={attribute}
                  />
                ))}
              </UI.GridContainer>
            </Guard>

            {registration.participant_fields.length > 0 && (
              <UI.GridContainer>
                {registration.participant_fields.map((field) => (
                  <ParticipantFieldValue
                    fieldEntry={{
                      value: null,
                      choice_entries: [],
                      field: {
                        id: field.id,
                      },
                      ...registration.participant_field_entries.filter(
                        (fieldEntry) => fieldEntry.field.id === field.id,
                      )[0],
                    }}
                    field={field}
                    key={field.id}
                  />
                ))}
              </UI.GridContainer>
            )}

            {!isOwner && (
              <UI.Div>
                <UI.Legend sc={{ fontWeight: 500 }}>
                  {t('ticket_owner')}
                </UI.Legend>
                <UI.Div>
                  <UI.Icon>
                    <UserIcon />
                  </UI.Icon>
                  {' '}
                  {registration.owner_name}
                </UI.Div>
              </UI.Div>
            )}

            {visibleUpgrades.length > 0 && (
              <UI.Delimit delimiter={<UI.HR sc={{ dashed: true }} />}>
                {visibleUpgrades.map((upgrade) => (
                  <UpgradeDetails upgrade={upgrade} key={upgrade.id} />
                ))}
              </UI.Delimit>
            )}

            {registration.fundraising_campaign && (
              <UI.Div>
                <UI.Legend sc={{ fontWeight: 500 }}>
                  {t('fundraising_page')}
                </UI.Legend>
                <UI.Div>
                  <UI.A
                    href={registration.fundraising_campaign.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {registration.charity?.title}
                    {' '}
                    <UI.Icon>
                      <ExternalLink />
                    </UI.Icon>
                  </UI.A>
                </UI.Div>
              </UI.Div>
            )}
          </UI.Delimit>
        </UI.FadeIn>
      )}
      {activeAction === 'edit_registration' && (
        <Guard
          scopedParticipant={summary.participant}
          title={getActionTitle(activeAction, registration, t)}
          sc={{ padding: [3, 4] }}
        >
          <EditRegistrationForm
            registration={registration}
            event={summary.event}
            reassignable={registration.is_reassignable && isOwner}
            candidates={registration.is_reassignable ? candidates : []}
            onSuccess={() => handleEdit()}
            onCancel={() => handleCancel()}
            refetchQueries={refetchQueries}
          />
        </Guard>
      )}
      {activeAction === 'exchange_ticket' && (
        <OwnerAction isOwner={isOwner} owner={registration.owner_name}>
          <Guard
            scopedParticipant={summary.participant}
            title={getActionTitle(activeAction, registration, t)}
            sc={{ padding: [3, 4] }}
          >
            <ExchangeInfo registration={registration} event={summary.event} />
          </Guard>
        </OwnerAction>
      )}
      {activeAction === 'assign_ticket' && (
        <OwnerAction isOwner={isOwner} owner={registration.owner_name}>
          <Guard
            scopedParticipant={summary.participant}
            title={getActionTitle(activeAction, registration, t)}
            sc={{ padding: [3, 4] }}
          >
            <TransferForm
              registration={registration}
              event={summary.event}
              openEditRegistrationTab={() => handleSetAction('edit_registration')}
            />
          </Guard>
        </OwnerAction>
      )}
      {(activeAction === 'resell_ticket') && (
        <OwnerAction isOwner={isOwner} owner={registration.owner_name}>
          <Guard
            scopedParticipant={summary.participant}
            title={getActionTitle(activeAction, registration, t)}
            sc={{ padding: [3, 4] }}
          >
            <ResaleForm registration={registration} event={summary.event} />
          </Guard>
        </OwnerAction>
      )}
      {activeAction === 'unassign_ticket' && (
        <OwnerAction isOwner={isOwner} owner={registration.owner_name}>
          <Guard
            scopedParticipant={summary.participant}
            title={getActionTitle(activeAction, registration, t)}
            sc={{ padding: [3, 4] }}
          >
            <UnassignRegistrationForm registration={registration} onSuccess={() => handleUnassign()} />
          </Guard>
        </OwnerAction>
      )}
      {activeAction === 'confirm_lottery' && (
        <OwnerAction isOwner={isOwner} owner={registration.owner_name}>
          <Guard
            scopedParticipant={summary.participant}
            title={getActionTitle(activeAction, registration, t)}
            sc={{ padding: [3, 4] }}
          >
            <ConfirmLotteryForm
              registration={registration}
              event={summary.event}
            />
          </Guard>
        </OwnerAction>
      )}
    </UI.Div>
  );
};

interface OwnerActionProps {
  isOwner: boolean;
  owner: string;
  children?: ReactNode;
}

const OwnerAction = ({ isOwner, owner, children }: OwnerActionProps) => {
  const { t } = useTranslation();

  if (!isOwner) {
    return (
      <UI.Div sc={{ padding: [3, 4] }}>
        <UI.Warning>
          {t('action_only_available_for_owner_warning', { owner })}
        </UI.Warning>
      </UI.Div>
    );
  }

  return <>{children}</>;
};

export interface RegistrationTitleProps {
  registration: BasicRegistration;
  summary: RegistrationSummary;
  primaryAction: Action | null;
  activeAction: Action | null;
  setAction: (action: Action | null) => void;
}

const RegistrationTitle = ({
  registration, summary, primaryAction, activeAction, setAction,
}: RegistrationTitleProps) => {
  const { t } = useTranslation();
  const { formatNumber, parseDate, formatDate } = useLocale();
  const formatTimeSlot = useTimeSlotFormatter();

  const hasTicketNumber = summary.event.ticket_numbers_published && registration.ticket_number;

  const upgrades = countItems(registration.upgrades.filter(
    (upgrade) => upgrade.product.type === ProductType.item,
  ).map((upgrade) => (
    `${upgrade.product.title}${upgrade.product_variant ? ` (${upgrade.product_variant.title})` : ''}`
  )));

  const isLocked = registration.locked;
  const isOwner = registration.owner.id === summary.participant.id;
  const isExpanded = !!activeAction;

  const actions: Action[] = [
    ...primaryAction !== 'overview' ? ['overview'] as const : [],
    'edit_registration',
    'assign_ticket',
    ...registration.is_unassignable && isOwner ? ['unassign_ticket'] as const : [],
    'exchange_ticket',
    'resell_ticket',
  ];

  const unavailableActions: Action[] = [];

  if (isLocked) {
    unavailableActions.push('edit_registration');
  }

  if (!registration.is_reassignable) {
    unavailableActions.push('assign_ticket');
  }

  if (!registration.exchange_code || !summary.event.active) {
    unavailableActions.push('exchange_ticket');
  }

  if (!registration.resale.resellable || !summary.event.active) {
    unavailableActions.push('resell_ticket');
  }

  const hasAvailableActions = actions.length - unavailableActions.length > 0;

  const { preregistration } = registration;

  const minutesLeftForConfirmation = preregistration?.draw_result
    ? differenceInMinutes(parseDate(preregistration.draw_result.end_date, { timezone: 'UTC' }), new Date())
    : 0;
  const hoursLeftForConfirmation = Math.floor(minutesLeftForConfirmation / 60);
  const daysLeftForConfirmation = Math.floor(hoursLeftForConfirmation / 24);

  return (
    <UI.Div>
      {registration.qr_url && (
        <UI.Div
          sc={{ textAlign: 'center' }}
          style={{
            transition: 'all 0.15s ease-in-out',
            height: isExpanded ? 211 : 0,
            opacity: isExpanded ? 1 : 0,
            overflow: 'hidden',
          }}
        >
          <DownloadTicketButton
            href={registration.ticket_url}
            target="_blank"
            rel="noopener"
            sc={{
              disabled: !summary.event.tickets_available,
            }}
          >
            <img
              src={`${registration.qr_url}&size=300`}
              width="100%"
              height="100%"
              alt={t('qr_code')}
              style={{ display: 'block', opacity: 0.8 }}
            />
            <UI.Div sc={{ mt: 0.5, fontWeight: 500 }}>
              <UI.Icon>
                <Download />
              </UI.Icon>
              {' '}
              {t('download_pdf')}
            </UI.Div>
          </DownloadTicketButton>
          <UI.HR sc={{ dashed: true, my: 3 }} />
        </UI.Div>
      )}
      <UI.GridContainer sc={{ columns: (isExpanded || !registration.qr_url) ? '1fr' : '42px 1fr', gutter: 0.5 }}>
        {!isExpanded && registration.qr_url && (
          <UI.FadeIn
            style={{
              padding: 5,
              border: '1px solid rgba(0, 0, 0, .15)',
              borderRadius: 3,
              width: 42,
              height: 42,
            }}
          >
            <img
              src={`${registration.qr_url}&size=300`}
              width="100%"
              height="100%"
              alt={t('qr_code')}
              style={{ display: 'block', opacity: registration.assigned ? 0.8 : 0.5 }}
            />
          </UI.FadeIn>
        )}
        <UI.Div sc={{ pr: [4, 6] }} style={{ minWidth: 0 }}>
          <UI.H4 sc={{ color: !registration.assigned ? 'gray.500' : undefined }}>
            {registration.assigned
              ? registration.full_name
              : (registration.invited_email || t('not_assigned_yet'))}
            {!registration.assigned && registration.invited_email && ` ${t('invited_parentheses')} `}
          </UI.H4>
          <UI.FlexContainer sc={{ wrap: 'wrap' }}>
            <SellableLabel
              sc={{ mt: 0.5, mr: 1 }}
              style={{ minWidth: 0 }}
              error={!registration.assigned}
            >
              <UI.Truncate>
                <UI.Icon>
                  <UI.Icons.Ticket />
                </UI.Icon>
                {' '}
                <UI.Delimit>
                  {registration.ticket.title}
                  {preregistration?.lottery_status === LotteryStatus.pending && t('pending')}
                  {registration.preregistered_by && t('confirmed')}
                </UI.Delimit>
                {isOwner && registration.purchase.batch_size > 1 && (
                  <UI.Span style={{ fontWeight: 300, opacity: 0.7 }}>
                    {` (${formatNumber(registration.purchase.unit_id)}/${formatNumber(registration.purchase.batch_size)})`}
                  </UI.Span>
                )}
              </UI.Truncate>
            </SellableLabel>
            {registration.time_slot && (
              <UI.Label sc={{ small: true, mt: 0.5, mr: 1 }}>
                <UI.Icon>
                  <Clock />
                </UI.Icon>
                {' '}
                {formatTimeSlot(registration.time_slot)}
              </UI.Label>
            )}
            {hasTicketNumber && (
              <UI.Label sc={{ small: true, mt: 0.5 }}>
                <UI.Icon>
                  <UI.Icons.StartFinish />
                </UI.Icon>
                {' '}
                {registration.ticket_number}
              </UI.Label>
            )}
          </UI.FlexContainer>
        </UI.Div>
      </UI.GridContainer>
      {(registration.team || upgrades.length > 0) && (
        <>
          <UI.HR sc={{ dashed: true, my: isExpanded ? 3 : 2 }} />
          <UI.Div sc={{ color: 'gray.500', small: true }}>
            {registration.team && (
              <UI.Span sc={{ mr: 2 }}>
                <UI.Icon>
                  <Users />
                </UI.Icon>
                {' '}
                {registration.team.title}
              </UI.Span>
            )}
            {upgrades.map((upgrade) => (
              <UI.Span sc={{ mr: 2 }} key={upgrade}>
                <UI.Icon>
                  <CornerDownRight />
                </UI.Icon>
                {' '}
                {upgrade}
              </UI.Span>
            ))}
          </UI.Div>
        </>
      )}
      {summary.event.active && summary.event.enable_public_resale && registration.resale.public && (
        <UI.Info sc={{ mt: 2 }}>
          <UI.FlexContainer sc={{ justifyContent: 'space-between', alignItems: 'center' }}>
            <UI.Span>
              {t('ticket_for_sale_message')}
            </UI.Span>
            {isOwner && (
              <UI.Div sc={{ padding: 1, margin: -1, relative: true }} style={{ zIndex: 300 }}>
                <UI.Button
                  onClick={() => setAction('resell_ticket')}
                  sc={{ brand: 'secondary', size: 'sm', active: activeAction === 'resell_ticket' }}
                >
                  {t('edit')}
                </UI.Button>
              </UI.Div>
            )}
          </UI.FlexContainer>
        </UI.Info>
      )}
      {preregistration?.lottery_status === LotteryStatus.selected && preregistration.draw_result && (
        <UI.Success sc={{ relative: true, mt: 2 }} icon={null} style={{ overflow: 'hidden' }}>
          <img
            src={assetUrl('images/confetti.svg')}
            style={{
              position: 'absolute',
              inset: 0,
              display: 'block',
              objectFit: 'cover',
              objectPosition: 'center',
              zIndex: 1,
              opacity: 0.2,
            }}
            alt={t('lottery_selected_title')}
          />
          <UI.Div style={{ position: 'relative', zIndex: 2 }}>
            <UI.H5>
              {t('lottery_selected_title')}
            </UI.H5>
            {preregistration.draw_result.end_date && (
              <UI.Span>
                <Trans
                  i18nKey={preregistration.draw_result.amount > 0
                    ? 'lottery_selected_description'
                    : 'lottery_selected_description_free'}
                  values={{
                    timeLeft: hoursLeftForConfirmation > 24
                      ? t('n_days', {
                        count: daysLeftForConfirmation, value: formatNumber(daysLeftForConfirmation),
                      })
                      : (minutesLeftForConfirmation > 60
                        ? t('n_hours', {
                          count: hoursLeftForConfirmation, value: formatNumber(hoursLeftForConfirmation),
                        })
                        : t('n_minutes', {
                          count: minutesLeftForConfirmation, value: formatNumber(minutesLeftForConfirmation),
                        })),
                    date: formatDate(parseDate(preregistration.draw_result.end_date, { timezone: 'UTC' })),
                  }}
                >
                  <UI.Strong />
                </Trans>
              </UI.Span>
            )}
          </UI.Div>
        </UI.Success>
      )}
      {preregistration?.lottery_status === LotteryStatus.expired && (
        <UI.Warning sc={{ relative: true, mt: 2 }}>
          <UI.H5>
            {t('lottery_expired_title')}
          </UI.H5>
          <UI.Paragraph sc={{ mb: 0 }}>
            {t('lottery_expired_description')}
          </UI.Paragraph>
        </UI.Warning>
      )}
      {preregistration?.lottery_status === LotteryStatus.rejected && (
        <UI.Warning sc={{ relative: true, mt: 2 }}>
          <UI.H5>
            {t('lottery_rejected_title')}
          </UI.H5>
          <UI.Paragraph sc={{ mb: 0 }}>
            {t('lottery_rejected_description')}
          </UI.Paragraph>
        </UI.Warning>
      )}
      <UI.HR sc={{ dashed: true, my: isExpanded ? 3 : 2 }} />
      <UI.FlexContainer
        sc={{
          spaceX: 1,
          relative: true,
          // Negative margin to create a 'hitbox' around the button
          // that prevents accidental toggling of the dropdown section.
          margin: -1,
          padding: 1,
        }}
        style={{ zIndex: 300 }}
      >
        <UI.Button
          onClick={() => setAction(primaryAction)}
          sc={{ brand: 'secondary', size: 'sm', active: activeAction === primaryAction }}
          disabled={activeAction === primaryAction}
        >
          <UI.Icon>
            {actionIcons[primaryAction]()}
          </UI.Icon>
          {' '}
          {primaryAction === 'overview' ? t('view') : getActionTitle(primaryAction, registration, t)}
        </UI.Button>

        {hasAvailableActions && (
          <OptionsDropdown
            trigger={(
              <UI.Button
                sc={{ size: 'sm', active: activeAction && activeAction !== primaryAction }}
              >
                <UI.Icon>
                  <MoreVertical />
                </UI.Icon>
                {' '}
                {t('actions')}
                {' '}
                <UI.Icon>
                  <ChevronDown />
                </UI.Icon>
              </UI.Button>
            )}
            align="start"
          >
            {actions.map((action) => (
              <Option
                icon={actionIcons[action]()}
                label={getActionTitle(action, registration, t)}
                onClick={() => setAction(action)}
                active={activeAction === action}
                disabled={unavailableActions.includes(action)}
                key={action}
              />
            ))}
          </OptionsDropdown>
        )}
      </UI.FlexContainer>
    </UI.Div>
  );
};

const DownloadTicketButton = styled(UI.A)`
  ${({ theme }) => css`
    display: inline-block;
    padding: ${theme.gutter * 0.25}px;
    border-radius: ${theme.borderRadiuses.md}px;
    border: 1px solid ${theme.colors.gray[200]};
    max-width: 150px;
    color: ${theme.colors.gray[800]};
    transition: all 0.15s ease-in-out;

    &:hover {
      background: ${theme.colors.gray[50]};
      border-color: ${theme.colors.gray[400]};
    }
  `}
`;

const getActionTitle = (action: Action, registration: { assigned: boolean; }, t: TFunction) => {
  if (action === 'assign_ticket' && registration.assigned) {
    return t('reassign_ticket');
  }

  return t(action);
};

const actionIcons = {
  overview: () => <UI.Icons.Ticket />,
  edit_registration: () => <Edit />,
  assign_ticket: () => <UserPlus />,
  resell_ticket: () => <UI.Icons.Resale />,
  exchange_ticket: () => <Repeat />,
  unassign_ticket: () => <UserX />,
  confirm_lottery: () => <CheckCircle />,
};

export default RegistrationDetails;
